import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { RxHamburgerMenu } from "react-icons/rx";
import { IoClose } from "react-icons/io5";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import API_BASE_URL from "../config";
import RightSidebar from "../component/RightSidebar";
import "../css/header.css";
import { setUser } from "../actions/userAction";
import { setHeaderMenus } from "../actions/headerrAction";

const Header = ({ menuItems, user, setUser, setHeaderMenus }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [hideHeader, setHideHeader] = useState(false);

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);
  const closeSidebar = () => setSidebarOpen(false);

  /* ================= ADMIN PAGE BODY CLASS ================= */
  useEffect(() => {
    document.body.classList.remove("admin-page");

    if (location.pathname.startsWith("/admin")) {
      document.body.classList.add("admin-page");
    }

    return () => {
      document.body.classList.remove("admin-page");
    };
  }, [location.pathname]);

  /* ================= LOGOUT ================= */
  const handleLogout = async () => {
    try {
      await axios.post(
        `${API_BASE_URL}/api/logout`,
        {},
        { withCredentials: true }
      );

      setUser(null);
      setHeaderMenus([]);
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  /* ================= FETCH HEADER MENUS ================= */
  useEffect(() => {
    const fetchHeaderMenus = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/headerMenus`,
          { withCredentials: true }
        );

        if (Array.isArray(response.data.headerMenus)) {
          setHeaderMenus(response.data.headerMenus);
        }
      } catch (error) {
        console.error("Error fetching header menus:", error);
      }
    };

    fetchHeaderMenus();
  }, [setHeaderMenus, user]);

  /* ================= CHECK SESSION ================= */
  useEffect(() => {
    const checkSession = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/check-session`,
          { withCredentials: true }
        );

        if (response.data.loggedIn) {
          const { user_id, email, username, role } = response.data;
          setUser({ user_id, email, username, role });
        }
      } catch (error) {
        console.error("Session check failed:", error);
      }
    };

    checkSession();
  }, [setUser]);

  /* ================= HIDE NAV ON SCROLL ================= */
  useEffect(() => {
    if (typeof window === "undefined") return;

    const scrollContainer =
      document.querySelector(".component-scroll-container") || null;

    const handleScroll = () => {
      const windowScroll =
        window.scrollY ||
        document.documentElement.scrollTop ||
        document.body.scrollTop ||
        0;

      const containerScroll = scrollContainer
        ? scrollContainer.scrollTop
        : 0;

      setHideHeader(windowScroll > 0 || containerScroll > 0);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll, {
        passive: true,
      });
    }

    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollContainer) {
        scrollContainer.removeEventListener("scroll", handleScroll);
      }
    };
  }, [location.pathname]);

  /* ================= CLOSE SIDEBAR ON SUBCATEGORY ================= */
  useEffect(() => {
    if (location.pathname.startsWith("/subcategory/")) {
      setSidebarOpen(false);
    }
  }, [location.pathname]);

  /* ================= ROUTE CHECKS ================= */
  const isAuthPage =
    location.pathname === "/login" || location.pathname === "/register";

  const isMultiStepForm = location.pathname === "/multistep";

  return (
    <>
      <header
        className={`header ${
          isAuthPage || isMultiStepForm ? "dark-header" : ""
        }`}
      >
        {/* LOGO */}
        <Link to="/" style={{ textDecoration: "none" }}>
          <div className="logo">
            {menuItems[0]?.value ? (
              <img
                src={menuItems[0].value}
                alt={menuItems[0].title || "Logo"}
                className="logo-image"
              />
            ) : (
              <>
                {menuItems[0]?.title}
                <sup>®</sup>
              </>
            )}
          </div>
        </Link>

        {/* ================= DESKTOP NAV ================= */}
        {!isMultiStepForm && (
          <nav
            className={`nav-links ${
              hideHeader ? "hide-on-scroll" : ""
            }`}
          >
            {menuItems.slice(1, 5).map((menu, index) => (
              <Link key={index} to={menu.path}>
                {menu.title}
              </Link>
            ))}

            {user ? (
              <div className="user-container">
                <span className="user-email">
                  Welcome, {user.username}
                </span>
                <button
                  className="logout-button"
                  onClick={handleLogout}
                >
                  Logout
                </button>
              </div>
            ) : (
              <Link to="/login" className="signin-link">
                {menuItems[5]?.title || "Login"}
              </Link>
            )}
          </nav>
        )}

        {/* ================= MOBILE ICON ================= */}
        <div className="icons">
          <div className="menu-icon" onClick={toggleSidebar}>
            {sidebarOpen ? <IoClose /> : <RxHamburgerMenu />}
          </div>
        </div>
      </header>

      {/* ================= MOBILE SIDEBAR ================= */}
      <RightSidebar
        isOpen={sidebarOpen}
        toggleSidebar={closeSidebar}
        menuItems={menuItems}
        user={user}
        onLogout={handleLogout}
      />
    </>
  );
};

const mapStateToProps = (state) => ({
  menuItems: state.headerr.menus || [],
  user: state.user?.user || null,
});

export default connect(mapStateToProps, {
  setUser,
  setHeaderMenus,
})(Header);
